import { GQL } from '@aline/graphql';

/**
 * Verb placed after the actor name for each lifecycle event, e.g. "completed the task". Kept in
 * sync with the feed's STATUS_EVENT_META (packages/client/.../activity-status-meta.tsx). `satisfies`
 * makes this exhaustive over ActivityStatusEventType; the Map lets callers look up by the raw string
 * value (GQL or Apollo enums share values) without a cast.
 */
const VERB_BY_EVENT = {
  [GQL.ActivityStatusEventType.TaskCreated]: 'created the task',
  [GQL.ActivityStatusEventType.TaskAssigned]: 'assigned the task',
  [GQL.ActivityStatusEventType.TaskCompleted]: 'completed the task',
  [GQL.ActivityStatusEventType.TaskReopened]: 'reopened the task',
  [GQL.ActivityStatusEventType.TaskPhaseChanged]: 'moved the task to a new phase',
  [GQL.ActivityStatusEventType.TaskEdited]: 'edited the task',
  [GQL.ActivityStatusEventType.TaskChangesRequested]: 'requested changes',
  [GQL.ActivityStatusEventType.TaskReReviewRequested]: 'requested re-review',
  [GQL.ActivityStatusEventType.TaskChangesReminderSent]: 'sent a reminder',
  [GQL.ActivityStatusEventType.TaskReviewReminderSent]: 'reminded the reviewers',
  [GQL.ActivityStatusEventType.TaskReviewRequestWithdrawn]: 'took back the review request',
  [GQL.ActivityStatusEventType.TaskSubmittedForReview]: 'requested review',
  [GQL.ActivityStatusEventType.TaskApproved]: 'approved the task',
  [GQL.ActivityStatusEventType.TaskStarted]: 'started the task',
  [GQL.ActivityStatusEventType.TaskMissingAssignee]:
    'started a task with reviewers but nobody to do the work',
  [GQL.ActivityStatusEventType.TaskRejected]: 'rejected the task',
  [GQL.ActivityStatusEventType.TaskInvitationSent]: 'sent the task to external assignees',
  [GQL.ActivityStatusEventType.TaskFileUploaded]: 'uploaded a file',
  [GQL.ActivityStatusEventType.TaskAssigneeCompleted]: 'completed their part of the task',
  [GQL.ActivityStatusEventType.TaskAssigneeRejected]: 'rejected the task as an optional assignee',
  [GQL.ActivityStatusEventType.TaskAssigneeCompletionUndone]: 'took back their completion',
  [GQL.ActivityStatusEventType.WorkflowCreated]: 'created the workflow',
  [GQL.ActivityStatusEventType.WorkflowStarted]: 'started the workflow',
  [GQL.ActivityStatusEventType.WorkflowCompleted]: 'completed the workflow',
  [GQL.ActivityStatusEventType.WorkflowPriorityChanged]: 'changed the priority',
  [GQL.ActivityStatusEventType.WorkflowOwnerChanged]: 'changed the owner',
  [GQL.ActivityStatusEventType.WorkflowSentForSignature]: 'sent the workflow for signature',
  [GQL.ActivityStatusEventType.WorkflowRevertedToDraft]: 'reverted the workflow to draft',
  [GQL.ActivityStatusEventType.WorkflowSigned]: 'signed the workflow',
} satisfies Record<GQL.ActivityStatusEventType, string>;

const STATUS_EVENT_VERB = new Map<string, string>(Object.entries(VERB_BY_EVENT));

export interface ActivityActionArgs {
  entityType: 'Task' | 'Workflow';
  /** GQL/Apollo `ActivityItemType` value (string). */
  itemType: string;
  /** Resolved TASK_ASSIGNED sides, see {@link StatusEventDescriptor.assignment}. */
  assignment?: TaskAssignmentSides | null;
  /** See {@link StatusEventDescriptor.assignmentChange}. */
  assignmentChange?: GQL.ActivityAssignmentChange | null;
  /** GQL/Apollo `ActivityStatusEventType` value (string), for STATUS_UPDATE items. */
  statusEventType?: string | null;
}

/** One coalesced lifecycle event within a STATUS_UPDATE row. */
export interface StatusEventDescriptor {
  /** GQL/Apollo `ActivityStatusEventType` value (string). */
  eventType: string;
  // Who a TASK_ASSIGNED event added and removed, on each side of the task.
  assignment?: TaskAssignmentSides | null;
  /**
   * What a TASK_ASSIGNED event did to the assignee list. Unassignment, AI
   * delegation and a cancelled coalesced run all share that event type, so
   * without this they would all read as a plain assignment.
   */
  assignmentChange?: GQL.ActivityAssignmentChange | null;
}

/** People one side of an assignment change named. */
export interface AssignmentSide {
  // How many the payload named, including accounts that no longer resolve.
  count: number;
  // Display names, with stand-ins for the unresolved (see {@link withUnresolvedNames}).
  names: string[];
  // The one person named is the actor, as in "self-assigned the task".
  isActor?: boolean;
}

export interface TaskAssignmentSides {
  addedAssignees: AssignmentSide;
  addedReviewers: AssignmentSide;
  // People who stayed on the task and switched to doing the work.
  madeAssignees: AssignmentSide;
  // People who stayed on the task and switched to reviewing it.
  madeReviewers: AssignmentSide;
  removedAssignees: AssignmentSide;
  removedReviewers: AssignmentSide;
}

/**
 * One clause of an activity sentence, as in "added | Jane Park | as a reviewer". The name sits
 * apart so the feed can bold it.
 */
export interface ActivityClause {
  lead: string;
  targetName?: string;
  trail?: string;
}

/**
 * The clauses of an assignment change, in the order they read: "assigned the task to Bo Chen and
 * added Jane Park as a reviewer". Empty when the change named nobody. Shared by the feed, the bell
 * and the email so they say the same thing.
 */
export function getTaskAssignmentClauses(sides: TaskAssignmentSides): ActivityClause[] {
  // After "assigned the task", "it" already means the task and a name is needed to make sense.
  const followsAssign = sides.addedAssignees.count > 0;
  const orderedClauses: [AssignmentSide, (side: AssignmentSide) => ActivityClause][] = [
    [
      sides.addedAssignees,
      side =>
        getSideClause(side, {
          self: 'self-assigned the task',
          unnamed: 'assigned the task',
          verb: 'assigned the task to',
        }),
    ],
    [sides.addedReviewers, side => getReviewerClause(side, 'added')],
    [
      sides.madeReviewers,
      side => getMadeClause(side, { plural: 'reviewers', singular: 'a reviewer' }),
    ],
    [
      sides.madeAssignees,
      side => getMadeClause(side, { plural: 'assignees', singular: 'an assignee' }),
    ],
    [
      sides.removedAssignees,
      side =>
        getSideClause(side, {
          self: followsAssign ? 'unassigned themselves' : 'unassigned themselves from the task',
          unnamed: followsAssign ? 'unassigned someone else' : 'unassigned the task',
          verb: followsAssign ? 'unassigned it from' : 'unassigned the task from',
        }),
    ],
    [sides.removedReviewers, side => getReviewerClause(side, 'removed')],
  ];

  return orderedClauses
    .filter(([side]) => side.count > 0)
    .map(([side, toClause]) => toClause(side));
}

/** Joins clauses the way names are joined: "a and b", "a, b, and c". */
export function formatActivityClauses(clauses: readonly ActivityClause[]): string {
  return clauses
    .map((clause, index) => {
      const text = [clause.lead, clause.targetName, clause.trail].filter(Boolean).join(' ');
      return `${getActivityClauseSeparator(index, clauses.length)}${text}`;
    })
    .join('');
}

// What goes before the clause at `index`, so the feed can join its rich clauses the same way.
export function getActivityClauseSeparator(index: number, count: number): string {
  if (index === 0) return '';
  if (index < count - 1) return ', ';
  return count === 2 ? ' and ' : ', and ';
}

/**
 * Human phrase describing an activity event, placed after the actor name — the single source used
 * by the notification email and the in-app bell (and matching the feed's copy), so all three read
 * identically, e.g. "completed the task", "assigned the task to Bob Lee", "commented on the
 * workflow". Params are raw strings so both the server's `GQL` enums and the client's `Apollo`
 * enums (which share values) can pass their values directly.
 */
export function getActivityActionText({
  assignment,
  assignmentChange,
  entityType,
  itemType,
  statusEventType,
}: ActivityActionArgs): string {
  const entityNoun = entityType === 'Task' ? 'task' : 'workflow';

  if (itemType === GQL.ActivityItemType.Comment) return `commented on the ${entityNoun}`;
  if (itemType === GQL.ActivityItemType.AgentThread)
    return `started an Aline AI thread on the ${entityNoun}`;
  if (!statusEventType) return `updated the ${entityNoun}`;

  return getStatusEventPhrase(entityNoun, {
    assignment,
    assignmentChange,
    eventType: statusEventType,
  });
}

/**
 * Joined phrase for a (possibly coalesced) STATUS_UPDATE row's events, matching the feed — e.g.
 * "completed the task and reopened the task". Used by the email + bell so a coalesced follow-up
 * event reads correctly instead of showing only the first event.
 */
export function getStatusEventsActionText(
  entityType: 'Task' | 'Workflow',
  events: StatusEventDescriptor[]
): string {
  const entityNoun = entityType === 'Task' ? 'task' : 'workflow';
  const phrases = events.map(event => getStatusEventPhrase(entityNoun, event));
  const last = phrases.at(-1);
  if (!last) return `updated the ${entityNoun}`;
  if (phrases.length === 1) return last;
  return `${phrases.slice(0, -1).join(', ')} and ${last}`;
}

/**
 * Joins assignee names for activity copy: "Ana", "Ana and Bo", "Ana, Bo, and Cy".
 * Shared so the feed row, the notification email, and the bell read the same.
 */
export function formatActivityNameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`;
}

/**
 * Stands in for assignees the payload named whose accounts no longer resolve,
 * so a partial list never reads as the whole one. Empty when nothing resolved:
 * there is no honest way to name people we cannot look up.
 */
export function withUnresolvedNames(names: string[], namedCount: number): string[] {
  const unresolved = namedCount - names.length;
  if (unresolved <= 0 || names.length === 0) return names;
  return [...names, unresolved === 1 ? '1 other' : `${unresolved} others`];
}

function getStatusEventPhrase(
  entityNoun: string,
  { assignment, assignmentChange, eventType }: StatusEventDescriptor
): string {
  if (eventType === GQL.ActivityStatusEventType.TaskAssigned) {
    if (assignmentChange === GQL.ActivityAssignmentChange.AiDelegated)
      return 'delegated the task to Aline AI';
    if (assignmentChange === GQL.ActivityAssignmentChange.AiDelegationRemoved)
      return 'removed Aline AI delegation';
    const clauses = assignment ? getTaskAssignmentClauses(assignment) : [];
    if (clauses.length > 0) return formatActivityClauses(clauses);
    if (assignmentChange === GQL.ActivityAssignmentChange.Updated)
      return 'updated the task assignees';
  }
  return STATUS_EVENT_VERB.get(eventType) ?? `updated the ${entityNoun}`;
}

function getSideClause(
  side: AssignmentSide,
  copy: { self: string; unnamed: string; verb: string }
): ActivityClause {
  if (side.isActor) return { lead: copy.self };
  const [firstName] = side.names;
  if (!firstName) return { lead: copy.unnamed };
  return { lead: copy.verb, targetName: formatActivityNameList(side.names) };
}

function getMadeClause(
  side: AssignmentSide,
  role: { plural: string; singular: string }
): ActivityClause {
  const isPlural = side.count > 1;
  if (side.isActor) return { lead: `made themselves ${role.singular}` };
  if (side.names.length === 0)
    return { lead: isPlural ? `made others ${role.plural}` : `made someone ${role.singular}` };
  return {
    lead: 'made',
    targetName: formatActivityNameList(side.names),
    trail: isPlural ? role.plural : role.singular,
  };
}

function getReviewerClause(side: AssignmentSide, verb: 'added' | 'removed'): ActivityClause {
  const isPlural = side.count > 1;
  if (side.isActor) return { lead: `${verb} themselves as a reviewer` };
  if (side.names.length === 0) return { lead: `${verb} ${isPlural ? 'reviewers' : 'a reviewer'}` };
  return {
    lead: verb,
    targetName: formatActivityNameList(side.names),
    trail: isPlural ? 'as reviewers' : 'as a reviewer',
  };
}
